import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MainLayout from '../../../components/layout/MainLayout';
import { useAuth } from '../../../contexts/AuthContext';
import { getRankedProfile, getRatingHistory } from '../../../services/rankedService';
import { RankedProfile, RatingHistoryEvent } from '../../../types/ranked';
import RankedProfilePanel from '../../../components/ranked/RankedProfilePanel';
import RatingDelta from '../../../components/ranked/RatingDelta';

/** Ladder gold — fixed, for the same reason tier colours are. */
const GOLD_ACCENT = '#F39C12';

/**
 * A student's ranked standing — their own, or a peer's reached from a
 * leaderboard row.
 *
 * The peer case is why this page reads `visibility` rather than assuming the
 * profile is the viewer's own. It previously rendered whoever's ID was in the
 * URL under first-person copy with nothing naming them, so another student's
 * standing read as your own. Rating history is fetched only for the student's
 * own profile — the endpoint requires full visibility and 403s otherwise.
 */
const RankedProfilePage: React.FC = () => {
  const { userId: userIdParam } = useParams<{ userId?: string }>();
  const { authState } = useAuth();
  const user = authState.user;
  const theme = useTheme();

  const userId = userIdParam ? Number(userIdParam) : user?.id;
  const isOwnProfile = userId != null && userId === user?.id;

  const [profile, setProfile] = useState<RankedProfile | null>(null);
  const [history, setHistory] = useState<RatingHistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      return;
    }

    setLoading(true);
    Promise.all([
      getRankedProfile(userId),
      isOwnProfile ? getRatingHistory(userId) : Promise.resolve([] as RatingHistoryEvent[])
    ])
      .then(([profileData, historyData]) => {
        setProfile(profileData);
        setHistory(historyData);
        setError(null);
      })
      .catch(() => setError('Could not load this ranked profile.'))
      .finally(() => setLoading(false));
  }, [userId, isOwnProfile]);

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (error || !profile) {
    return (
      <MainLayout>
        <Alert severity="error">{error ?? 'No profile found.'}</Alert>
      </MainLayout>
    );
  }

  const latest = history[0];
  const subjectName = profile.displayName ?? 'This mathlete';

  return (
    <MainLayout>
      {/* Hero, following the v2 page recipe */}
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: 4,
          borderRadius: 4,
          background: `linear-gradient(135deg, ${alpha(GOLD_ACCENT, 0.1)} 0%, ${alpha(
            theme.palette.primary.main,
            0.1
          )} 100%)`,
          border: `1px solid ${alpha(GOLD_ACCENT, 0.2)}`
        }}
      >
        <Typography
          variant="h3"
          fontWeight={800}
          sx={{
            background: `linear-gradient(135deg, ${GOLD_ACCENT}, ${theme.palette.primary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <EmojiEventsIcon
            sx={{ fontSize: 40, color: GOLD_ACCENT }}
          />
          {isOwnProfile ? 'Ranked Mathlete' : subjectName}
        </Typography>
        <Typography variant="h6" color="text.secondary" fontWeight={400}>
          {isOwnProfile
            ? `Beat mathletes rated above you to climb. ${profile.season.name}.`
            : `Ranked Mathlete · ${profile.season.name}.`}
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <RankedProfilePanel
            profile={profile}
            latestDelta={latest?.delta ?? null}
            voice={isOwnProfile ? 'second' : 'third'}
            subjectName={subjectName}
          />
        </Grid>
        {/* Recent results */}
        {history.length > 0 && (
          <Grid item xs={12}>
            <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                    color: 'text.secondary'
                  }}
                >
                  Recent quizzes
                </Typography>

                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {history.map(event => (
                    <Box
                      key={event.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        px: 2,
                        py: 1.25,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={600} noWrap>
                          {event.quizTitle ?? 'Quiz'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Finished {ordinal(event.placement)} of {event.fieldSize}
                        </Typography>
                      </Box>
                      <RatingDelta delta={event.delta} showUnit />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </MainLayout>
  );
};

function ordinal(n: number): string {
  const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
}

export default RankedProfilePage;
